import { useMemo } from "react"
import { flatJoin } from "shared/util"

import { useSearch } from "features/search/context"
import { KojinResult } from "interface/kojin"
import { Section, SectionProps } from "components/controls/section"
import { Stack, Typography, Link } from "@mui/material"

type KojinGuideLinesProps = {
	kojin: KojinResult | null
} & SectionProps
export const KojinGuideLines = ({
	kojin,
	...others
}: KojinGuideLinesProps) => {
	const search = useSearch()
	const master = useMemo(
		() => search?.guideLine ?? [],
		[search]
	)
	const guideLineTypes = useMemo(
		() => {
			if (!master || !kojin || !kojin.guideLine || kojin.guideLine.length === 0) return []

			const kojinsGuideLines: {
				guideLineType: string
				guideLines: {
					guideLineId: number
					guideLineName: string
					url: string | null
					details: {
						researcherClass?: string
						postNames?: string
					}[]
				}[]
			}[] = []
			for(const { guideLineId, researcherClass, postNames } of kojin.guideLine) {
				const guideLineMaster = master.find(m => m.guideLineId === guideLineId)
				if (!guideLineMaster) continue

				const guideLineName = guideLineMaster.guideLineName
				const guideLineType = guideLineMaster.guideLineType ?? ''
				const url = guideLineMaster.sourceUrl
				let type = kojinsGuideLines.find(e => e.guideLineType === guideLineType)
				if (!type) {
					type = {
						guideLineType,
						guideLines: []
					}
					kojinsGuideLines.push(type)
				}
				let guideLine = type.guideLines.find(e => e.guideLineId === guideLineId)
				if (!guideLine) {
					guideLine = {
						guideLineId: guideLineId,
						guideLineName,
						url,
						details: []
					}
					type.guideLines.push(guideLine)
				}
				guideLine.details.push({
					researcherClass,
					postNames
				})
			}
			return kojinsGuideLines
		},
		[master, kojin]
	)
	return guideLineTypes.map(
		({ guideLineType, guideLines }) => (
			<Section
				key={`type-${guideLineType}`}
				label={guideLineType ?? "ガイドライン"}
				{...others}
			>
				<Stack direction="column" spacing={0.5}>
					{guideLines.map(({ guideLineName, url, details }, i) => (
						<Stack
							key={`guide-${i}`}
							direction="row"
							flexWrap="wrap"
						>
							{url ? (
								<Link href={url} target="_blank" variant="body2" underline='hover' marginRight={1}>
									{guideLineName}
								</Link>
							) : (
								<Typography variant="body2" color='text.primary' component="span" marginRight={1}>
									{guideLineName}
								</Typography>
							)}
							{details.map(({ researcherClass, postNames }, j, arr) => 
								<Typography key={`guide-${i}-${j}`} variant="body2" color='text.primary' component="span">
									{flatJoin(' ', researcherClass, postNames)}
									{(j < (arr.length - 1)) ? "、" : ""}
								</Typography>
							)}
						</Stack>
					))}
				</Stack>
			</Section>
		)
	)
}
